import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BellAlertIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { pushService } from '@/services/push.service';
import type {
  PushCategory,
  PushSeverity,
  ResolvedCategoryPreference,
  CategoryPreferenceUpdate,
} from '@/types/push';

const WEB_TOKEN_KEY = 'vf.push.webToken';

const CATEGORY_LABELS: Record<PushCategory, string> = {
  incident: 'Incidents',
  threat: 'Threats',
  executive_protection: 'Executive Protection',
  investigation: 'Investigations',
  trip: 'Trip Alerts',
};

const SEVERITY_LABELS: Record<PushSeverity, string> = {
  low: 'Low & above',
  medium: 'Medium & above',
  high: 'High & above',
  critical: 'Critical only',
};

interface Banner {
  title: string;
  message: string;
  route: string;
  severity: PushSeverity;
}

/**
 * VIS-188 — Live Security Updates settings.
 *
 * The executive registers this device for push (a simulated web token on the
 * web target), tunes per-category preferences (balanced default: medium-and-
 * above), and can fire a test notification. On web the delivered "push"
 * surfaces as an in-app banner whose "View" action taps through to the
 * relevant screen.
 */
export default function PushNotificationSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [webToken, setWebToken] = useState<string | null>(() => localStorage.getItem(WEB_TOKEN_KEY));
  const [draft, setDraft] = useState<ResolvedCategoryPreference[]>([]);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [testCategory, setTestCategory] = useState<PushCategory>('incident');
  const [testSeverity, setTestSeverity] = useState<PushSeverity>('high');
  const [testStatus, setTestStatus] = useState<string | null>(null);

  const isRegistered = Boolean(webToken);

  const { data: prefsData } = useQuery({
    queryKey: ['push-category-preferences'],
    queryFn: () => pushService.getCategoryPreferences(),
  });

  const { data: tokens } = useQuery({
    queryKey: ['push-tokens'],
    queryFn: () => pushService.listTokens(),
  });

  // Seed the editable draft from the server's resolved preferences.
  useEffect(() => {
    if (prefsData?.categories) {
      setDraft(prefsData.categories);
    }
  }, [prefsData]);

  const severities = useMemo<PushSeverity[]>(
    () => prefsData?.severities ?? ['low', 'medium', 'high', 'critical'],
    [prefsData]
  );

  const registerMutation = useMutation({
    mutationFn: () => {
      const token = `web-${crypto.randomUUID()}`;
      return pushService.registerToken({
        token,
        platform: 'web',
        deviceName: navigator.userAgent.slice(0, 120),
      });
    },
    onSuccess: (created) => {
      setWebToken(created.token);
      localStorage.setItem(WEB_TOKEN_KEY, created.token);
      queryClient.invalidateQueries({ queryKey: ['push-tokens'] });
      toast.success('Push enabled on this device');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to enable push');
    },
  });

  const unregisterMutation = useMutation({
    mutationFn: (token: string) => pushService.unregisterToken(token),
    onSuccess: () => {
      setWebToken(null);
      localStorage.removeItem(WEB_TOKEN_KEY);
      queryClient.invalidateQueries({ queryKey: ['push-tokens'] });
      toast.success('Push disabled on this device');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to disable push');
    },
  });

  const saveMutation = useMutation({
    mutationFn: (preferences: CategoryPreferenceUpdate[]) => pushService.updateCategoryPreferences(preferences),
    onSuccess: (data) => {
      setDraft(data.categories);
      queryClient.setQueryData(['push-category-preferences'], data);
      toast.success('Notification preferences saved');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to save preferences');
    },
  });

  const testSendMutation = useMutation({
    mutationFn: () =>
      pushService.testSend({
        category: testCategory,
        severity: testSeverity,
        resourceId: 'demo-1',
      }),
    onSuccess: (result) => {
      if (result.delivered && result.notification && result.route) {
        setBanner({
          title: result.notification.title,
          message: result.notification.message,
          route: result.route,
          severity: result.notification.severity,
        });
        setTestStatus(null);
      } else if (result.reason === 'suppressed_by_preference') {
        setBanner(null);
        setTestStatus('Suppressed by your preferences for this category/severity.');
      } else if (result.reason === 'no_registered_device') {
        setBanner(null);
        setTestStatus('No registered device — enable push on this device first.');
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to send test notification');
    },
  });

  const updateDraft = (category: PushCategory, patch: Partial<ResolvedCategoryPreference>) => {
    setDraft((prev) =>
      prev.map((p) => (p.category === category ? { ...p, ...patch, isDefault: false } : p))
    );
  };

  const handleSave = () => {
    saveMutation.mutate(
      draft.map((p) => ({ category: p.category, pushEnabled: p.pushEnabled, minSeverity: p.minSeverity }))
    );
  };

  const handleTapThrough = () => {
    if (banner) {
      const route = banner.route;
      setBanner(null);
      navigate(route);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Live Security Updates</h1>
        <p className="text-secondary-600 dark:text-secondary-400 mt-1">
          Register this device for push and tune which categories notify you.
        </p>
      </div>

      {/* In-app push banner (web fallback for a delivered push) */}
      {banner && (
        <div
          data-testid="push-banner"
          role="alert"
          className="rounded-lg border-l-4 border-l-primary-500 bg-primary-50 dark:bg-primary-900/20 p-4 flex items-start justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            <BellAlertIcon className="w-6 h-6 text-primary-600 dark:text-primary-300 mt-0.5" />
            <div>
              <p className="font-semibold text-secondary-900 dark:text-white">{banner.title}</p>
              <p className="text-sm text-secondary-700 dark:text-secondary-300">{banner.message}</p>
              <span className="text-xs font-medium uppercase text-primary-700 dark:text-primary-300">
                {banner.severity}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" data-testid="push-banner-view" onClick={handleTapThrough}>
              View
            </Button>
            <Button variant="ghost" size="icon-sm" aria-label="Dismiss" onClick={() => setBanner(null)}>
              <XMarkIcon className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Device registration */}
      <Card>
        <CardHeader>
          <CardTitle>Device Registration</CardTitle>
          <CardDescription>
            {isRegistered
              ? 'This device is registered to receive push notifications.'
              : 'Enable push to receive live security updates on this device.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          {isRegistered ? (
            <>
              <span data-testid="push-status" className="text-sm font-medium text-success-600 dark:text-success-400">
                Push enabled on this device
              </span>
              <Button
                variant="outline"
                size="sm"
                data-testid="disable-push"
                disabled={unregisterMutation.isPending}
                onClick={() => webToken && unregisterMutation.mutate(webToken)}
              >
                Disable push
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              data-testid="enable-push"
              disabled={registerMutation.isPending}
              onClick={() => registerMutation.mutate()}
            >
              Enable push on this device
            </Button>
          )}
          <span className="text-xs text-secondary-500 dark:text-secondary-400">
            {tokens?.length ?? 0} registered device{(tokens?.length ?? 0) === 1 ? '' : 's'}
          </span>
        </CardContent>
      </Card>

      {/* Category preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Category Preferences</CardTitle>
          <CardDescription>
            Balanced default: push for medium-and-above severity in every category. Tune per category below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {draft.map((pref) => (
            <div
              key={pref.category}
              data-testid={`pref-row-${pref.category}`}
              className="flex flex-wrap items-center justify-between gap-3 border-b border-secondary-100 dark:border-secondary-800 pb-3 last:border-0"
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  data-testid={`pref-enabled-${pref.category}`}
                  checked={pref.pushEnabled}
                  onChange={(e) => updateDraft(pref.category, { pushEnabled: e.target.checked })}
                  className="h-4 w-4 rounded border-secondary-300 text-primary-600"
                />
                <span className="text-sm font-medium text-secondary-900 dark:text-white">
                  {CATEGORY_LABELS[pref.category]}
                </span>
                {pref.isDefault && (
                  <span className="text-xs text-secondary-400">(default)</span>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-secondary-600 dark:text-secondary-400">Push at</span>
                <select
                  data-testid={`pref-severity-${pref.category}`}
                  aria-label={`${CATEGORY_LABELS[pref.category]} minimum severity`}
                  value={pref.minSeverity}
                  disabled={!pref.pushEnabled}
                  onChange={(e) => updateDraft(pref.category, { minSeverity: e.target.value as PushSeverity })}
                  className="rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-800 px-2 py-1 text-sm disabled:opacity-50"
                >
                  {severities.map((s) => (
                    <option key={s} value={s}>
                      {SEVERITY_LABELS[s]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ))}
          <div className="pt-2">
            <Button
              size="sm"
              data-testid="save-preferences"
              disabled={saveMutation.isPending || draft.length === 0}
              onClick={handleSave}
            >
              Save preferences
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test send */}
      <Card>
        <CardHeader>
          <CardTitle>Send a Test Notification</CardTitle>
          <CardDescription>
            Delivers a simulated push to this device (respecting your preferences) and taps through to the content.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary-600 dark:text-secondary-400">Category</span>
              <select
                data-testid="test-category"
                aria-label="Test category"
                value={testCategory}
                onChange={(e) => setTestCategory(e.target.value as PushCategory)}
                className="rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-800 px-2 py-1"
              >
                {(Object.keys(CATEGORY_LABELS) as PushCategory[]).map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary-600 dark:text-secondary-400">Severity</span>
              <select
                data-testid="test-severity"
                aria-label="Test severity"
                value={testSeverity}
                onChange={(e) => setTestSeverity(e.target.value as PushSeverity)}
                className="rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-800 px-2 py-1"
              >
                {severities.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <Button
              size="sm"
              data-testid="send-test"
              disabled={testSendMutation.isPending}
              onClick={() => testSendMutation.mutate()}
            >
              Send test notification
            </Button>
          </div>
          {testStatus && (
            <p data-testid="test-status" className="text-sm text-warning-600 dark:text-warning-400">
              {testStatus}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
